import { isEqual } from "./comparison";

export interface AccrualLineSnapshot {
    id: string;
    sourceId?: string | null;
    description?: string | null;
    amount?: number | null;
    unitAmount?: number | null;
    quantityDecimal?: number | null;
    currency?: string | null;
    accountId?: string | null;
    accountsPayableId?: string | null;
    accountsReceivableId?: string | null;
    classIds?: string[] | null;
}

export type AccrualLineField = keyof Omit<
    AccrualLineSnapshot,
    "id" | "sourceId"
>;

export type AccrualLineFieldValue = AccrualLineSnapshot[AccrualLineField];

export const ACCRUAL_LINE_COMPARABLE_FIELDS: AccrualLineField[] = [
    "description",
    "amount",
    "unitAmount",
    "quantityDecimal",
    "currency",
    "accountId",
    "accountsPayableId",
    "accountsReceivableId",
    "classIds",
];

const SET_LIKE_FIELDS: ReadonlySet<AccrualLineField> = new Set(["classIds"]);

export interface AccrualLineDelta {
    id: string;
    type: "added" | "removed" | "updated";
    line: AccrualLineSnapshot;
    fieldChanges?: Array<{
        field: AccrualLineField;
        before: AccrualLineFieldValue;
        after: AccrualLineFieldValue;
    }>;
}

function normalizeForCompare(
    field: AccrualLineField,
    value: AccrualLineFieldValue,
): AccrualLineFieldValue {
    if (!SET_LIKE_FIELDS.has(field)) {
        return value;
    }
    if (!Array.isArray(value)) {
        return value;
    }
    return [...value].sort();
}

function isFieldEqual(
    field: AccrualLineField,
    a: AccrualLineFieldValue,
    b: AccrualLineFieldValue,
): boolean {
    return isEqual(
        normalizeForCompare(field, a),
        normalizeForCompare(field, b),
    );
}

/**
 * Compute the per-line deltas between two lines arrays captured in bill/invoice
 * audit logs. Lines are matched by `id`. Returns an array of additions,
 * removals, and per-field updates.
 */
export function computeAccrualLineDeltas(
    before: AccrualLineSnapshot[],
    after: AccrualLineSnapshot[],
): AccrualLineDelta[] {
    const beforeById = new Map(before.map((line) => [line.id, line]));
    const seen = new Set<string>();
    const deltas: AccrualLineDelta[] = [];

    for (const line of after) {
        seen.add(line.id);
        const previous = beforeById.get(line.id);
        if (!previous) {
            deltas.push({ id: line.id, type: "added", line });
            continue;
        }
        const fieldChanges = ACCRUAL_LINE_COMPARABLE_FIELDS.flatMap((field) => {
            const beforeValue = previous[field] ?? null;
            const afterValue = line[field] ?? null;
            if (isFieldEqual(field, beforeValue, afterValue)) {
                return [];
            }
            return [
                {
                    field,
                    before: beforeValue,
                    after: afterValue,
                },
            ];
        });
        if (fieldChanges.length > 0) {
            deltas.push({
                id: line.id,
                type: "updated",
                line,
                fieldChanges,
            });
        }
    }

    for (const line of before) {
        if (seen.has(line.id)) {
            continue;
        }
        deltas.push({ id: line.id, type: "removed", line });
    }

    return deltas;
}

export function toAccrualLineArray(raw: unknown): AccrualLineSnapshot[] {
    if (!Array.isArray(raw)) {
        return [];
    }
    return raw.filter(
        (item): item is AccrualLineSnapshot =>
            item !== null && typeof item === "object" && "id" in item,
    );
}
